import React from 'react';
import {useQuery} from '@tanstack/react-query';
import toLower from 'lodash/toLower';

import {useFeatureFlagsApi} from 'apps/feature-flags/src/api/feature-flags';
import {featureFlagsQueryKeyForList} from 'iron/apps/experiments/src/react-query';

import {useExperimentPageContext} from '../context';
import {useStagedExperiment} from './staged-experiment';

export const useQueryFeatureFlags = () => {
  const {projectId} = useExperimentPageContext();

  const {stagedExperiment} = useStagedExperiment();

  const {listFeatureFlags} = useFeatureFlagsApi({
    projectId,
  });

  const {data: featureFlags = []} = useQuery({
    queryKey: featureFlagsQueryKeyForList(projectId),
    queryFn: () => listFeatureFlags(),
  });

  const featureFlagId = stagedExperiment?.settings?.featureFlagSettings?.featureFlagId;
  const stagedFeatureFlag = React.useMemo(() => {
    if (!featureFlagId) {
      return undefined;
    }
    return featureFlags.find((flag) => flag.id === featureFlagId);
  }, [featureFlagId, featureFlags]);

  const variants = React.useMemo(() => {
    if (!stagedFeatureFlag) {
      return [];
    }
    return stagedFeatureFlag.ruleset?.variants ?? [];
  }, [stagedFeatureFlag, featureFlags]);

  const defaultVariant = React.useMemo(() => {
    if (!variants.length) {
      return undefined;
    }
    return (
      variants.find((variant) => variant.is_control) ??
      variants.find((variant) => toLower(variant.key).includes(`control`))
    );
  }, [variants]);

  return {
    featureFlags,
    stagedFeatureFlag,
    variants,
    defaultVariant,
  };
};
